/**
 * WGTeam Component - Working Group Team Members Display and Management - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Comprehensive team member management interface with advanced filtering, search capabilities,
 * and dual view modes (table/image grid). Features role-based sorting, company/cohort filtering,
 * member profile integration with working group details, and dynamic image loading with
 * fallback to initials display.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of team member data objects
 * @param {string|number} props.data[].id - Unique member identifier
 * @param {string} props.data[].firstName - Member's first name
 * @param {string} props.data[].lastName - Member's last name
 * @param {string} props.data[].email - Member's email address
 * @param {string} props.data[].workingGroupPosition - Member's role (Chair, Vice Chair, Member)
 * @param {string} props.data[].companyName - Member's company name
 * @param {string} props.data[].memberCohort - Member's cohort classification
 * @returns {JSX.Element} The WGTeam component
 * 
 * @example
 * <WGTeam data={teamMembersArray} />
 */

import React from 'react';
import { useState, useEffect } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ProfileModal from '../../modals/ProfileModal';
import apiHelper from '../../utils/apiHelper';
import { FILE_NAMES } from '../../utils/constants';
import './WGTeam.css';

function WGTeam({ data }) {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [memberWorkingGroups, setMemberWorkingGroups] = useState([]);
  const [filters, setFilters] = useState({
    role: 'all',
    company: 'all',
    cohort: 'all',
    search: ''
  });
  const [memberImages, setMemberImages] = useState({});

  // Get unique companies and cohorts for filter dropdowns
  const companies = [...new Set(data.map(member => member.companyName))].sort();
  const cohorts = [...new Set(data.map(member => member.memberCohort))].sort();

  /**
   * Filters team members based on current filter criteria
   * 
   * @param {Array} members - Array of member objects to filter
   * @returns {Array} Filtered array of members matching all criteria
   */
  const filterMembers = (members) => {
    return members.filter(member => {
      const matchesRole = filters.role === 'all' || member.workingGroupPosition === filters.role;
      const matchesCompany = filters.company === 'all' || member.companyName === filters.company;
      const matchesCohort = filters.cohort === 'all' || member.memberCohort === filters.cohort;
      const matchesSearch = !filters.search || 
        `${member.firstName} ${member.lastName}`.toLowerCase().includes(filters.search.toLowerCase());

      return matchesRole && matchesCompany && matchesCohort && matchesSearch;
    });
  };

  /**
   * Handles member profile click to show modal with working group details
   * Fetches additional working group data for the selected member
   * 
   * @param {Object} member - Member object containing profile information
   */
  const handleProfileClick = async (member) => {
    try {
      // Get all working groups for this member
      const response = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_MEMBER_DETAILS);
      if (response && response.fileContent) {
        const memberGroups = response.fileContent.filter(
          item => item.email === member.email
        );
        setMemberWorkingGroups(memberGroups);
      }

      setProfileData({ id: member.id });
      setShowProfileModal(true);
      
    } catch (error) {
      // Still show modal even if working groups fetch fails
      setShowProfileModal(true);
    }
  };

  /**
   * Generates initials from first and last names
   * 
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @returns {string} Uppercase initials (e.g., "JD" for John Doe)
   */
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  /**
   * Sorts members by role hierarchy and then alphabetically
   * Chair first, then Vice Chair, then alphabetical by last name
   * 
   * @param {Array} members - Array of member objects to sort
   * @returns {Array} Sorted array of members
   */
  const sortMembers = (members) => {
    return [...members].sort((a, b) => {
      if (a.workingGroupPosition === 'Chair') return -1;
      if (b.workingGroupPosition === 'Chair') return 1;
      
      if (a.workingGroupPosition === 'Vice Chair') return -1;
      if (b.workingGroupPosition === 'Vice Chair') return 1;
      
      const lastNameCompare = a.lastName.localeCompare(b.lastName);
      if (lastNameCompare !== 0) return lastNameCompare;
      
      return a.firstName.localeCompare(b.firstName);
    });
  };

  /**
   * Loads member profile images with authenticated API calls
   * Creates blob URLs for image display and manages loading states
   */
  const loadMemberImages = async () => {
    const imagePromises = data
      .filter(member => member.email)
      .map(async (member) => {
        try {
          // First get the profile to get the photo
          const profile = await apiHelper.getMemberProfileByEmail(member.email);
          if (!profile?.photo) {
            return null;
          }

          // Get the image using the photo filename
          const authToken = await apiHelper.getAuthToken();
          const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`, {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });

          if (!response.ok) {
            return null;
          }

          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
          return { email: member.email, url: imageUrl };
        } catch (error) {
          return null;
        }
      });

    const loadedImages = await Promise.all(imagePromises);
    const imageMap = {};
    loadedImages.forEach(img => {
      if (img) {
        imageMap[img.email] = img.url;
      }
    });
    setMemberImages(imageMap);
  };

  // Load member images on component mount and data changes
  useEffect(() => {
    loadMemberImages();

    return () => {
      Object.values(memberImages).forEach(url => {
        if (url?.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [data]);

  /**
   * Reloads all member images - used after profile updates
   */
  const reloadMemberImages = async () => {
    await loadMemberImages();
  };

  return (
    <>
      <div className="team-filters">
        <select
          value={filters.role}
          onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
          className="filter-select"
        >
          <option value="all">All Roles</option>
          <option value="Chair">Chair</option>
          <option value="Vice Chair">Vice Chair</option>
          <option value="Member">Member</option>
        </select>

        <select
          value={filters.company}
          onChange={(e) => setFilters(prev => ({ ...prev, company: e.target.value }))}
          className="filter-select"
        >
          <option value="all">All Companies</option>
          {companies.map(company => (
            <option key={company} value={company}>{company}</option>
          ))}
        </select>

        <select
          value={filters.cohort}
          onChange={(e) => setFilters(prev => ({ ...prev, cohort: e.target.value }))}
          className="filter-select"
        >
          <option value="all">All Cohorts</option>
          {cohorts.map(cohort => (
            <option key={cohort} value={cohort}>{cohort}</option>
          ))}
        </select>

       
          <div className="search-input-container">
            <svg 
              aria-hidden="true" 
              focusable="false" 
              data-prefix="fas" 
              data-icon="magnifying-glass" 
              className="svg-inline--fa fa-magnifying-glass search-icon" 
              role="img" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 512 512"
            >
              <path 
                fill="currentColor" 
                d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search members..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="filtersearch"
            />
          </div>
       
      </div>

      <Tabs>
        <TabList>
         
          <Tab><i className="fa-solid fa-list"></i> Table View</Tab>
          <Tab><i className="fa-solid fa-table-cells"></i> Image View</Tab>
        </TabList>

        <TabPanel>
          <div className="table-responsive">
            {filterMembers(sortMembers(data)).length > 0 ? (
              <table className="member-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Company</th>
                    <th>Cohort</th>
                  </tr>
                </thead>
                <tbody>
                  {filterMembers(sortMembers(data)).map((member) => (
                    <tr 
                      key={member.id} 
                      onClick={() => handleProfileClick(member)}
                      className="member-row"
                    >
                      <td className="member-photo-cell">
                        {memberImages[member.email] ? (
                          <img 
                            src={memberImages[member.email]}
                            alt={`${member.firstName} ${member.lastName}`}
                            className="member-table-photo"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.classList.add('member-table-initials');
                              e.target.parentElement.textContent = getInitials(member.firstName, member.lastName);
                            }}
                          />
                        ) : (
                          <div className="member-table-initials">
                            {getInitials(member.firstName, member.lastName)}
                          </div>
                        )}
                      </td>
                      <td>{`${member.firstName} ${member.lastName}`}</td>
                      <td>
                        <div className="role-cell">
                          {member.workingGroupPosition}
                          {member.workingGroupPosition === 'Chair' && (
                            <i className="fa-solid fa-star chair-icon"></i>
                          )}
                        </div>
                      </td>
                      <td>{member.companyName}</td>
                      <td>{member.memberCohort}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-members-message">
                <i className="fa-regular fa-user"></i>
                <p>No members found</p>
              </div>
            )}
          </div>
        </TabPanel>
        <TabPanel>
          <div className="member-grid">
            {filterMembers(sortMembers(data)).length > 0 ? (
              filterMembers(sortMembers(data)).map((member) => (
                <div 
                  key={member.id} 
                  onClick={() => handleProfileClick(member)} 
                  className="member-card"
                >
                  {memberImages[member.email] ? (
                    <img
                      src={memberImages[member.email]}
                      alt={`${member.firstName} ${member.lastName}`}
                      className="member-photo"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentElement.classList.add('wgmember-initials');
                        e.target.parentElement.textContent = getInitials(member.firstName, member.lastName);
                      }}
                    />
                  ) : (
                    <div className="wgmember-initials">
                      {getInitials(member.firstName, member.lastName)}
                    </div>
                  )}
                  <div className="member-info">
                    <h4>{`${member.firstName} ${member.lastName}`}</h4>
                    <div className="member-position">
                      {member.workingGroupPosition === 'Chair' ? (
                        <span className="chair-label">
                          <i className="fa-solid fa-star"></i> Chair
                        </span>
                      ) : (
                        <span>{member.workingGroupPosition}</span>
                      )}
                    </div>
                    <span className="member-company">{member.companyName}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-members-message">
                <i className="fa-regular fa-user"></i>
                <p>No members found</p>
              </div>
            )}
          </div>
        </TabPanel>

      
      </Tabs>

      {showProfileModal && (
        <ProfileModal
          show={showProfileModal}
          onClose={() => {
            setShowProfileModal(false);
            setMemberWorkingGroups([]); // Clear working groups when modal closes
          }}
          small={true}
          profileData={profileData}
          memberWorkingGroups={memberWorkingGroups}
          onImageUpdate={reloadMemberImages}
        />
      )}
    </>
  );
}

export default WGTeam; 